import { useState, useEffect } from 'react';
import './Pages.css';

function Contact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const faqs = [
    {
      question: 'What is your typical project timeline?',
      answer:
        "Project timelines vary based on scope and complexity. Most projects range from 2-12 weeks. We'll provide a detailed timeline during our initial consultation.",
    },
    {
      question: 'Do you work with clients remotely?',
      answer:
        'Yes, we work with clients across North America. All communication and collaboration happens through modern digital tools.',
    },
    {
      question: 'What technologies do you specialize in?',
      answer:
        "We specialize in modern web technologies including React, Python, Node.js, and cloud platforms. We select the best technology stack for each project's specific needs.",
    },
    {
      question: 'How do you handle project pricing?',
      answer:
        "We offer both fixed-price and hourly arrangements depending on project scope. After understanding your requirements, we'll provide a transparent proposal with clear deliverables.",
    },
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleFaqKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleFaq(index);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            // Unobserve after visibility to prevent repeated callbacks
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('.animate-on-scroll');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus('sending');

    const apiBase = process.env.REACT_APP_API_URL || '';
    const apiUrl = apiBase ? `${apiBase.replace(/\/+$/, '')}/api/contact` : '/api/contact';

    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message }),
      });

      if (res.ok) {
        const payload = await res.json().catch(() => null);
        if (payload?.sent || payload?.status === 'sent') {
          setStatus('sent');
          setName('');
          setEmail('');
          setMessage('');
          return;
        }
      }

      // Fallback to mailto if backend endpoint not present, misconfigured, or returns error
      setStatus('fallback');
      const mailto = `mailto:mitchbrown532@gmail.com?subject=${encodeURIComponent(
        `Contact from ${name || email}`
      )}&body=${encodeURIComponent(message + '\n\nFrom: ' + (name || '') + ' <' + (email || '') + '>')}`;
      window.location.href = mailto;
    } catch (err) {
      setStatus('fallback');
      const mailto = `mailto:mitchbrown532@gmail.com?subject=${encodeURIComponent(
        `Contact from ${name || email}`
      )}&body=${encodeURIComponent(message + '\n\nFrom: ' + (name || '') + ' <' + (email || '') + '>')}`;
      window.location.href = mailto;
    }
  }

  return (
    <main className="container contact-page">
      <h1 className="page-title">Contact Us</h1>
      <p className="page-intro">
        Have a project in mind or want to discuss how we can help? We'd love to hear from you.
      </p>

      <div className="contact-layout">
        <form
          className="card contact-form animate-on-scroll"
          onSubmit={handleSubmit}
          aria-label="Contact form"
        >
          <label htmlFor="name-input">
            Name
            <input
              id="name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              aria-required="true"
            />
          </label>

          <label htmlFor="email-input">
            Email
            <input
              id="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              aria-required="true"
            />
          </label>

          <label htmlFor="message-input">
            Message
            <textarea
              id="message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              required
              aria-required="true"
            />
          </label>

          <button type="submit" className="btn btn-primary" aria-busy={status === 'sending'}>
            {status === 'sending' ? 'Sending...' : 'Send Message'}
          </button>

          {status === 'sent' && (
            <p className="success" role="alert" aria-live="polite">
              Thank you for reaching out. We'll get back to you soon.
            </p>
          )}
          {status === 'fallback' && (
            <p className="warning" role="alert" aria-live="polite">
              Unable to reach our server — opening your mail client instead.
            </p>
          )}
        </form>

        <aside className="card contact-faq animate-on-scroll" aria-labelledby="faq-heading">
          <h2 id="faq-heading">Frequently Asked Questions</h2>

          {faqs.map((faq, index) => (
            <div key={index} className="faq-item">
              <button
                className="faq-question"
                onClick={() => toggleFaq(index)}
                onKeyDown={(e) => handleFaqKeyDown(e, index)}
                aria-expanded={openFaqIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <h3>{faq.question}</h3>
                <span className="faq-icon" aria-hidden="true">
                  {openFaqIndex === index ? '−' : '+'}
                </span>
              </button>
              {openFaqIndex === index && (
                <p id={`faq-answer-${index}`} className="faq-answer">
                  {faq.answer}
                </p>
              )}
            </div>
          ))}
        </aside>
      </div>
    </main>
  );
}

export default Contact;
